import React, { useState } from 'react';

const Gallery: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [collection, setCollection] = useState<'engagement' | 'savethedate'>('engagement');
  const [filter, setFilter] = useState<'all' | 'featured'>('featured');

  // Generate array of all engagement photos (49 photos)
  const engagementPhotos = [1,2,3,4,5,6,7,8,9,11,14,17,21,30,32,37,40,44,47,48,49,50,51,52,53,54,55,56,57,58,59,60,61,62,63,64,65,66,67,71,88,89,90,91,92,93,94,95].map(n => {
    const num = String(n).padStart(3, '0');
    return `/images/engagement/${num}-engagement.jpg`;
  });

  // Generate array of all Save The Date photos (41 photos)
  const saveTheDatePhotos = [1,3,7,10,12,19,20,21,22,23,24,25,27,28,31,37,38,39,40,48,55,56,57,60,61,62,63,64,66,67,68,70,71].map(n => {
    const num = String(n).padStart(3, '0');
    return `/images/save-the-date/${num}-savethedate.jpg`;
  });

  // Featured photos for engagement
  const featuredEngagement = [
    engagementPhotos[0], engagementPhotos[4], engagementPhotos[8], engagementPhotos[12],
    engagementPhotos[16], engagementPhotos[20], engagementPhotos[24], engagementPhotos[28],
    engagementPhotos[32], engagementPhotos[36], engagementPhotos[40], engagementPhotos[44],
  ];

  // Featured photos for Save The Date
  const featuredSaveTheDate = [
    saveTheDatePhotos[0], saveTheDatePhotos[3], saveTheDatePhotos[6], saveTheDatePhotos[9],
    saveTheDatePhotos[12], saveTheDatePhotos[15], saveTheDatePhotos[18], saveTheDatePhotos[21],
    saveTheDatePhotos[24], saveTheDatePhotos[27], saveTheDatePhotos[30], saveTheDatePhotos[33],
  ];

  // Determine which photos to display
  const currentCollection = collection === 'engagement' ? engagementPhotos : saveTheDatePhotos;
  const currentFeatured = collection === 'engagement' ? featuredEngagement : featuredSaveTheDate;
  const displayPhotos = filter === 'all' ? currentCollection : currentFeatured;

  return (
    <section id="gallery" className="py-12 md:py-20 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-center text-wedding-navy mb-3 md:mb-4">
          Our Journey to Paradise
        </h2>
        <p className="text-center text-gray-600 text-sm sm:text-base mb-6 md:mb-8 px-2">
          Photos from our Punta Cana sessions
        </p>

        {/* Collection Selector */}
        <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 mb-4 md:mb-6 px-4">
          <button
            onClick={() => {
              setCollection('engagement');
              setFilter('featured');
            }}
            className={`px-4 sm:px-6 md:px-8 py-2 md:py-3 rounded-full text-sm md:text-base font-semibold transition-all ${
              collection === 'engagement'
                ? 'bg-wedding-navy text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Engagement ({engagementPhotos.length})
          </button>
          <button
            onClick={() => {
              setCollection('savethedate');
              setFilter('featured');
            }}
            className={`px-4 sm:px-6 md:px-8 py-2 md:py-3 rounded-full text-sm md:text-base font-semibold transition-all ${
              collection === 'savethedate'
                ? 'bg-wedding-navy text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Save The Date ({saveTheDatePhotos.length})
          </button>
        </div>

        {/* Featured/All Filter */}
        <div className="flex justify-center gap-2 sm:gap-4 mb-8 md:mb-12 px-4">
          <button
            onClick={() => setFilter('featured')}
            className={`px-4 sm:px-6 py-2 rounded-full text-sm md:text-base transition-all ${
              filter === 'featured'
                ? 'bg-wedding-gold text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Featured
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 sm:px-6 py-2 rounded-full text-sm md:text-base transition-all ${
              filter === 'all'
                ? 'bg-wedding-gold text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All ({currentCollection.length})
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
          {displayPhotos.map((photo, index) => (
            <div
              key={index}
              className="relative overflow-hidden rounded-lg shadow-md cursor-pointer group"
              onClick={() => setSelectedImage(index)}
            >
              <img
                src={photo}
                alt={`Nelisa and Jesse ${index + 1}`}
                className="w-full h-48 sm:h-56 md:h-64 object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"></path>
                </svg>
              </div>
            </div>
          ))}
        </div>

        {selectedImage !== null && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-2 sm:p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-2 sm:top-4 right-2 sm:right-4 text-white text-3xl sm:text-4xl hover:text-wedding-gold z-10"
              onClick={() => setSelectedImage(null)}
            >
              &times;
            </button>
            <button
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 text-white text-3xl sm:text-4xl hover:text-wedding-gold z-10"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage((prev) =>
                  prev !== null && prev > 0 ? prev - 1 : displayPhotos.length - 1
                );
              }}
            >
              &#8249;
            </button>
            <img
              src={displayPhotos[selectedImage]}
              alt={`Nelisa and Jesse ${selectedImage + 1}`}
              className="max-h-full max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-white text-3xl sm:text-4xl hover:text-wedding-gold z-10"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage((prev) =>
                  prev !== null && prev < displayPhotos.length - 1 ? prev + 1 : 0
                );
              }}
            >
              &#8250;
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
